<!--
FranchiseSelectMultiple Component - Reusable Multiple Franchise Selection Component

USE CASES:
==========
- Use in forms where multiple franchise selection is needed
- Automatically loads franchises selected in the global filter (useFiltro)
- Respects filter changes and updates dynamically
- Supports v-model binding with array of IDs
- Perfect for payment subaccounts that serve multiple franchises

IMPORTANT:
===========
- This component shows ONLY franchises selected in the filter, not all franchises under user's permissions
- Automatically refreshes when filter is updated (listens to 'filter.updated' event)
- Waits for filter initialization before loading franchises

PROPS:
=======
- modelValue: Array of selected franchise IDs (v-model)
- elemId: Unique identifier for the select element
- label: Label text for the select (defaults to 'Franquias')
- placeholder: Placeholder text (defaults to 'Selecione as franquias')
- callback: Optional function to call when franchises change
- disabled: Boolean to disable the select
- required: Boolean indicating if field is required

EVENTS:
========
- update:modelValue: Emitted when franchise selection changes (array of IDs)
- franchises-change: Emitted with franchise IDs array when selection changes
-->

<template>
  <SelectListMultiple
    :label="computedLabel"
    :elem-id="elemId"
    :itens-select="franquias"
    :model-value="modelValue"
    :placeholder="computedPlaceholder"
    :disabled="disabled"
    :ng-required="required"
    :validation="() => !required || (modelValue && modelValue.length > 0)"
    :callback="handleFranchisesChange"
    :max-selected-labels="3"
    display="chip"
    @update:model-value="handleValueChange"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../utils/DebugLogger';
import { useFiltro } from '../composables/useFiltro';
import { eventBus } from '../eventBus';
import SelectListMultiple from './SelectListMultiple.vue';

interface SelectItem {
  ID: string | number;
  Name: string;
}

// Props
const props = defineProps({
  // Array of selected franchise IDs (v-model)
  modelValue: {
    type: Array as () => (string | number)[],
    default: () => []
  },

  // Unique identifier for the select element
  elemId: {
    type: String,
    default: 'franchise-select-multiple'
  },

  // Label text for the select
  label: {
    type: String,
    default: ''
  },

  // Placeholder text
  placeholder: {
    type: String,
    default: ''
  },

  // Optional callback function triggered when franchises change
  callback: {
    type: Function as any,
    default: null
  },

  // Whether the select is disabled
  disabled: {
    type: Boolean,
    default: false
  },

  // Whether the field is required
  required: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: (string | number)[]];
  'franchises-change': [franchiseIds: (string | number)[]];
}>();

// Composables
const { t } = useI18n();
const { getSelectedFranchisesSelectArray, initialized } = useFiltro();

// Reactive data
const franquias = ref<SelectItem[]>([]);
const isLoading = ref<boolean>(false);

// Computed
const computedLabel = computed((): string => {
  return props.label || t('Franquias');
});

const computedPlaceholder = computed((): string => {
  return props.placeholder || t('Selecione as franquias');
});

// Methods
const loadFranquias = async (): Promise<void> => {
  log('FranchiseSelectMultiple', 'loadFranquias', 'Loading franquias from filter');

  isLoading.value = true;

  try {
    // Wait for filter to be initialized
    if (!initialized.value) {
      log('FranchiseSelectMultiple', 'loadFranquias', 'Filter not initialized yet, waiting...');
      franquias.value = [];
      isLoading.value = false;
      return;
    }

    // Get selected franchises from filter
    const selectedFranchises = await getSelectedFranchisesSelectArray();

    if (!selectedFranchises || selectedFranchises.length === 0) {
      log('FranchiseSelectMultiple', 'loadFranquias', 'No franchises selected in filter');
      franquias.value = [];
      isLoading.value = false;
      return;
    }

    franquias.value = selectedFranchises;
    log('FranchiseSelectMultiple', 'loadFranquias', 'Loaded selected franquias:', franquias.value);
  } catch (error) {
    log('FranchiseSelectMultiple', 'loadFranquias', 'Error loading franquias:', error);
    franquias.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleFranchisesChange = (): void => {
  log('FranchiseSelectMultiple', 'handleFranchisesChange', 'Franchises changed to:', props.modelValue);

  emit('franchises-change', props.modelValue);

  // Call callback if provided
  if (props.callback && typeof props.callback === 'function') {
    (props.callback as (ids: (string | number)[]) => void)(props.modelValue);
  }
};

const handleValueChange = (value: (string | number)[] | null): void => {
  log('FranchiseSelectMultiple', 'handleValueChange', 'Value changed to:', value);
  emit('update:modelValue', value || []);
};

const handleFilterUpdated = (): void => {
  log('FranchiseSelectMultiple', 'event:filter.updated', 'Filter updated, reloading franquias');
  loadFranquias();
};

// Watch for filter to be initialized
watch(() => initialized.value, (isInitialized: boolean) => {
  if (isInitialized) {
    log('FranchiseSelectMultiple', 'watch:initialized', 'Filter initialized, loading franquias');
    loadFranquias();
  }
}, { immediate: true });

// Listen to filter updates
eventBus.on('filter.updated', handleFilterUpdated);

// Cleanup
onBeforeUnmount(() => {
  eventBus.off('filter.updated', handleFilterUpdated);
});

// Lifecycle
onMounted(() => {
  log('FranchiseSelectMultiple', 'mounted', 'Component mounted');
  // loadFranquias will be called by the watcher when filter is ready
});
</script>

<style scoped>
/* No additional styles needed - inherits from SelectListMultiple */
</style>
